import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { Send, X, Loader2, Trash2, Brain, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import CrisisBanner from "@/components/CrisisBanner";

const QUICK_PROMPTS = [
  "What is ADHD?",
  "Tips for managing focus",
  "How do ADHD medications work?",
  "Coping with procrastination",
  "ADHD and sleep issues",
  "ADHD in adults vs children",
];

function detectCrisis(text) {
  const keywords = ["suicide", "suicidal", "kill myself", "end my life", "self-harm", "self harm", "don't want to live", "want to die"];
  return keywords.some(k => text.toLowerCase().includes(k));
}

export default function ADHDAssistantCard({ inline = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(QUICK_PROMPTS.slice(0, 3));
  const bottomRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      API.get("/adhd-assistant/history").then(({ data }) => {
        if (data?.length > 0) setMessages(data);
      }).catch(() => {});
    }
  }, [isOpen, messages.length]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { id: `u-${Date.now()}`, role: "user", content: msg }]);
    setLoading(true);
    setSuggestions([]);

    try {
      const { data } = await API.post("/adhd-assistant/chat", { message: msg });
      setMessages(prev => [...prev, { id: `a-${Date.now()}`, role: "assistant", content: data.response }]);
      if (data.suggestions?.length > 0) setSuggestions(data.suggestions);
    } catch (err) {
      setMessages(prev => [...prev, { id: `e-${Date.now()}`, role: "assistant", content: "I'm having trouble responding right now. Please try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    await API.delete("/adhd-assistant/history").catch(() => {});
    setMessages([]);
    setSuggestions(QUICK_PROMPTS.slice(0, 3));
  };

  // Inline card (shown on Home/Resources)
  if (!isOpen && !inline) return null;

  if (!isOpen && inline) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full bg-card border border-border rounded-xl p-4 hover:shadow-lg transition-all text-left group"
        data-testid="adhd-assistant-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center shrink-0">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-medium text-sm group-hover:text-primary transition-colors">ADHD Education Assistant</h4>
            <p className="text-xs text-muted-foreground mt-1">Get evidence-based answers about ADHD, focus strategies, medications & more</p>
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className={inline ? "bg-card border border-border rounded-xl overflow-hidden animate-fade-in" : "fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center"} data-testid="adhd-assistant-panel">
      <div className={inline ? "" : "w-full max-w-md h-[85vh] md:h-[70vh] bg-card rounded-t-2xl md:rounded-2xl flex flex-col shadow-2xl animate-fade-in"}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gradient-to-r from-primary to-primary/90 text-primary-foreground rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <Brain className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">ADHD Education Assistant</h3>
              <p className="text-[10px] opacity-75">Evidence-based info only</p>
            </div>
          </div>
          <div className="flex gap-1">
            {messages.length > 0 && (
              <button onClick={handleClear} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" title="Clear chat" data-testid="clear-chat-button">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" data-testid="close-assistant-button">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className={`flex-1 overflow-y-auto px-4 py-3 space-y-3 ${inline ? "max-h-96" : ""}`}>
          <CrisisBanner />
          {messages.length === 0 && (
            <div className="text-center py-8 space-y-3">
              <Brain className="w-10 h-10 text-muted-foreground mx-auto" />
              <div>
                <p className="font-medium text-sm">Ask me anything about ADHD</p>
                <p className="text-xs text-muted-foreground mt-1">I provide evidence-based information to support your journey</p>
              </div>
            </div>
          )}

          {messages.map((msg, i) => {
            const isUser = msg.role === "user";
            const isCrisis = msg.role === "assistant" && (msg.content.includes("988") || msg.content.includes("crisis"));
            return (
              <div key={msg.id || `h-${i}`} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                  isUser ? "bg-primary text-primary-foreground rounded-br-md"
                    : isCrisis ? "bg-red-50 border border-red-200 text-red-900 rounded-bl-md"
                    : "bg-accent text-foreground rounded-bl-md"
                }`}>
                  {isCrisis && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                      <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide">Important Resources</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            );
          })}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-accent rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1.5"><span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} /><span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} /><span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} /></div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && !loading && (
          <div className="px-4 py-2 border-t border-border flex gap-1.5 overflow-x-auto">
            {suggestions.map((s, i) => (
              <button key={s} onClick={() => handleSend(s)}
                className="shrink-0 px-3 py-1.5 rounded-full bg-accent text-foreground text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
                data-testid={`suggestion-${i}`}>
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 border-t border-border">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask about ADHD..."
              rows={1}
              className="flex-1 resize-none bg-accent rounded-xl px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="assistant-input"
            />
            <Button
              size="icon"
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="rounded-xl h-10 w-10 shrink-0"
              data-testid="assistant-send-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
            For educational purposes only. Always consult your provider for medical advice.
          </p>
        </div>
      </div>
    </div>
  );
}
